
import React from 'react';
import { Project } from '@/types/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Clock, UserCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface ProjectReviewListProps {
  projects: Project[];
  isLoading: boolean;
  selectedProjectId?: string;
  onSelectProject: (project: Project) => void;
  emptyMessage: string;
}

// Extending the Project type to include the joined user_profiles data
interface ProjectWithUserProfile extends Project {
  user_profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

const ProjectReviewList: React.FC<ProjectReviewListProps> = ({
  projects,
  isLoading,
  selectedProjectId,
  onSelectProject,
  emptyMessage
}) => {
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <div className="flex justify-between mt-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/4" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-2 divide-y">
      {projects.map((project) => {
        // Cast to the extended type to access user_profiles
        const projectWithProfile = project as unknown as ProjectWithUserProfile;
        
        return (
          <div
            key={project.id}
            className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
              selectedProjectId === project.id ? 'bg-muted' : ''
            }`}
            onClick={() => onSelectProject(project)}
          >
            <div className="flex justify-between items-start">
              <div className="font-medium">{project.title}</div>
              {project.score !== null ? (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <Check className="h-3 w-3 mr-1" />
                  Reviewed
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <Clock className="h-3 w-3 mr-1" />
                  Pending
                </Badge>
              )}
            </div>
            
            <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {project.description || 'No description provided'}
            </div>
            
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <div className="flex items-center">
                <UserCircle className="h-3 w-3 mr-1" />
                {projectWithProfile.user_profiles?.first_name || ''} {projectWithProfile.user_profiles?.last_name || 'Unknown User'}
              </div>
              <div>
                {project.updated_at && formatDistanceToNow(new Date(project.updated_at), { addSuffix: true })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectReviewList;
